/**
 * Server-side access code + PIN sign-in with brute-force protection.
 *
 * The PIN is only 6 digits, so the credential check cannot happen in the
 * browser where an attacker controls the loop. Sign-in runs here instead: a
 * server-owned counter records genuine failures (the server, not the client,
 * decides what counts as a failure) and locks a code out for a growing window
 * once the failures stack up. Failure messages never distinguish "no such
 * code" from "wrong PIN" from "locked", so nothing about an account leaks.
 */

import { createServerFn } from "@tanstack/react-start";

import { isValidAccessCode, isValidPin, normalizeAccessCode, codeToEmail } from "@/lib/access-code";

/** Failures allowed before any lockout kicks in. */
const FREE_ATTEMPTS = 5;
/** First lockout window, doubling per subsequent failure. */
const BASE_LOCK_SECONDS = 60;
const MAX_LOCK_SECONDS = 30 * 60;
/** A quiet spell resets the counter so honest typos don't accumulate forever. */
const COUNTER_TTL_SECONDS = 60 * 60;

export type SignInResult =
  | { ok: true; accessToken: string; refreshToken: string }
  | { ok: false; retryAfterSeconds: number };

const lockSecondsFor = (failures: number) => {
  if (failures <= FREE_ATTEMPTS) return 0;
  const step = failures - FREE_ATTEMPTS - 1;
  return Math.min(BASE_LOCK_SECONDS * 2 ** step, MAX_LOCK_SECONDS);
};

export const signInWithAccessCode = createServerFn({ method: "POST" })
  .inputValidator((input: { code: string; pin: string }) => input)
  .handler(async ({ data }): Promise<SignInResult> => {
    const code = normalizeAccessCode(data.code ?? "");
    // Malformed input never touches the auth engine or the counter.
    if (!isValidAccessCode(code) || !isValidPin(data.pin ?? "")) {
      return { ok: false, retryAfterSeconds: 0 };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const now = Date.now();

    const { data: row } = await supabaseAdmin
      .from("signin_throttle")
      .select("code, failures, first_failure_at, locked_until")
      .eq("code", code)
      .maybeSingle();

    if (row?.locked_until) {
      const remaining = Math.ceil((new Date(row.locked_until).getTime() - now) / 1000);
      if (remaining > 0) return { ok: false, retryAfterSeconds: remaining };
    }

    // A stale run of failures is forgotten rather than held against the user.
    const staleRun =
      row?.first_failure_at != null &&
      now - new Date(row.first_failure_at).getTime() > COUNTER_TTL_SECONDS * 1000;
    const priorFailures = !row || staleRun ? 0 : (row.failures ?? 0);

    const { createClient } = await import("@supabase/supabase-js");
    const authClient = createClient(
      process.env["SUPABASE_URL"]!,
      process.env["SUPABASE_PUBLISHABLE_KEY"]!,
      { auth: { persistSession: false, autoRefreshToken: false, storage: undefined } },
    );

    const { data: session, error } = await authClient.auth.signInWithPassword({
      email: codeToEmail(code),
      password: data.pin,
    });

    if (error || !session.session) {
      const failures = priorFailures + 1;
      const lockSeconds = lockSecondsFor(failures);
      await supabaseAdmin.from("signin_throttle").upsert(
        {
          code,
          failures,
          first_failure_at:
            priorFailures === 0
              ? new Date(now).toISOString()
              : (row?.first_failure_at ?? new Date(now).toISOString()),
          locked_until: lockSeconds ? new Date(now + lockSeconds * 1000).toISOString() : null,
          updated_at: new Date(now).toISOString(),
        },
        { onConflict: "code" },
      );
      return { ok: false, retryAfterSeconds: lockSeconds };
    }

    if (row) await supabaseAdmin.from("signin_throttle").delete().eq("code", code);

    return {
      ok: true,
      accessToken: session.session.access_token,
      refreshToken: session.session.refresh_token,
    };
  });
